/* eslint-disable promise/avoid-new */
import { ApiError } from './types.js';
import { StatusCode } from 'status-code-enum';

// Shared transport-level transient-retry primitive for idempotent registry/user
// reads. Extracted from api/agent/registry.ts so BOTH the agent-registry reads
// AND the boot-critical user fetch (auth/utils.ts fetchStudioUser) can share ONE
// retry layer with identical classification — a transient ITG blip on the user
// fetch was dead-ending the synchronizer into didFallback → the B2 "Unable to
// connect" boot guard (flaky e2e failure, ENG-4250). Centralising avoids drift
// between the two call sites.
//
// NOTE: we deliberately do NOT use p-retry. p-retry v5.1.2's selective-abort
// requires its NAMED `AbortError` export (`shouldRetry` only landed in v6), and a
// named import `{ AbortError } from 'p-retry'` FAILS under jest's experimental-vm
// ESM loader ("does not provide an export named 'AbortError'"), breaking
// test-studio for every test that transitively loads this module. Node resolves
// the named export fine — so the app works — but CI's jest does not. So we
// hand-roll a tiny bounded retry (full control, no fragile interop).
// Per rule: think-in-systems + capture-and-maintain-knowledge (ai.knowledge/global)
// — see esm-jest-mock-patterns.

const isFetchTypeError = (e: unknown): boolean =>
  e instanceof Error && e.name === 'TypeError' && /failed to fetch/i.test(e.message);

/**
 * Classify an error as a TRANSIENT network failure that is safe to retry on an
 * idempotent read.
 *
 * Returns true ONLY for:
 *  (a) an underlying fetch TypeError — `name === 'TypeError'` and the message
 *      matches /failed to fetch/i. We check this on BOTH the error itself AND
 *      `error.cause`, because `wrapApiCall` (api/utils.ts) re-wraps an
 *      Error-instance cause as `new ApiError(cause.message, { cause })` with NO
 *      `.code`. So after wrapping, the original network TypeError survives only
 *      on the ApiError's `.cause` (and its message is copied onto the ApiError).
 *  (b) a 5xx gateway error — `ApiError` with code exactly 502/503.
 *      NOT 504. API Gateway 504 means the Lambda already ran to its 29s
 *      integration timeout (ITG listRooms saturating Aurora Serverless v2 at
 *      8 ACU, 2026-08-29). Retrying it (transport 6 attempts × boot 10) is
 *      what turns one slow query into a 531-connection stampede. 502/503 are
 *      still short failures worth a cold-start retry.
 *
 * Returns FALSE for everything else. CRITICALLY this excludes every 4xx: a 403
 * (StatusCode.ClientErrorForbidden) is the cross-account signal owned end-to-end
 * by the auto-link controller's transient-403 re-probe (commit 50bf7f4f0). The
 * registry Lambda runs its policy check BEFORE the existence lookup, so a 403
 * means "exists under an inaccessible org". Retrying it at transport would
 * compress/mask the temporal 403->200 signal the re-probe discriminates on
 * (TIME is the only discriminator between transient replica lag and a genuine
 * cross-account link), regressing ENG-4154. 401/404 are likewise never retried
 * (404 also preserves fetchRoomById's return-undefined path).
 *
 * Per rule: ai-code-requires-tests + reference-rules-in-comments (ai.knowledge/global).
 *
 * @param error - The thrown error to classify.
 * @returns true iff the error is a transient network failure safe to retry.
 */
export const isTransientNetworkError = (error: unknown): boolean => {
  // Gateway timeout already burned ~29s of Lambda+RDS. Never retry.
  // Check BEFORE Failed-to-fetch: Chromium surfaces a 504 as CORS Failed to
  // fetch when the error page has no ACAO, and wrapApiCall may copy "HTTP 504"
  // onto the ApiError message.
  if (isGatewayTimeout(error)) {
    return false;
  }
  // (a) raw fetch TypeError, or an ApiError whose message/cause carries it.
  if (isFetchTypeError(error)) {
    return true;
  }
  if (error instanceof ApiError) {
    // wrapApiCall copies the cause's message onto the ApiError and stashes the
    // original TypeError on `.cause` (with no `.code`). Detect both.
    if (/failed to fetch/i.test(error.message) || isFetchTypeError(error.cause)) {
      return true;
    }
    // (b) short 5xx transients only — 502/503, NEVER 4xx, NEVER 504.
    if (
      error.code === StatusCode.ServerErrorBadGateway ||
      error.code === StatusCode.ServerErrorServiceUnavailable
    ) {
      return true;
    }
  }
  return false;
};

/** True when the error is an API Gateway integration timeout (HTTP 504). */
export const isGatewayTimeout = (error: unknown): boolean => {
  if (error instanceof ApiError && error.code === StatusCode.ServerErrorGatewayTimeout) {
    return true;
  }
  const message =
    error instanceof Error
      ? `${error.message} ${error.cause instanceof Error ? error.cause.message : ''}`
      : '';
  return /HTTP 504/i.test(message);
};

// Bounded transport-level retry budget for transient registry/user READ blips.
// Sized to absorb an ITG Lambda COLD START, not just a sub-second flap: a cold
// agent-registry/user-registry Lambda (or a brief gateway 503 burst) commonly
// takes several seconds to warm, longer than the previous ~1.1s budget which
// exhausted mid-cold-start and tripped the boot guard. 5 retries => 6 attempts;
// 200/400/800/1600/2000(capped)ms backoff => ~5.0s of waiting worst case. This
// stays comfortably under synchronizer.tsx's AUTH_GATE_TIMEOUT_MS (15s) which
// wraps the boot-critical user fetch, so a genuinely-down backend still rejects
// into the existing fallback rather than hanging. On a NON-transient error
// (e.g. a 403) the loop rethrows the ORIGINAL error IMMEDIATELY (attempt 0,
// code/cause intact) — so the first 403 still reaches the auto-link re-probe
// and the synchronizer .catch() exactly as before.
// Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
export const TRANSIENT_RETRY = {
  retries: 5,
  minTimeoutMs: 200,
  factor: 2,
  maxTimeoutMs: 2_000
} as const;

const delayMs = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wrap an idempotent (GET-shaped) read in a bounded retry that fires ONLY for
 * transient network failures (fetch TypeError / 5xx). Non-transient errors —
 * every 4xx, notably 403 — rethrow the ORIGINAL error unchanged on the FIRST
 * attempt (code + cause intact). Mutations MUST NOT be wrapped (retrying a
 * create/link could double-create rooms/links — the ENG-4154 duplicate class).
 *
 * Hand-rolled (no p-retry) to avoid the jest-ESM named-import break — see the
 * note at the top of this module. Per rule: ai-code-requires-tests (ai.knowledge/global).
 *
 * @param fn - The idempotent read to execute and possibly retry.
 * @returns The resolved value of `fn`.
 */
export const withTransientRetry = async <T>(fn: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isTransientNetworkError(error) || attempt >= TRANSIENT_RETRY.retries) {
        throw error;
      }
      console.warn('transient read failed, retrying:', (error as Error)?.message);
      const backoff = Math.min(
        TRANSIENT_RETRY.maxTimeoutMs,
        TRANSIENT_RETRY.minTimeoutMs * TRANSIENT_RETRY.factor ** attempt
      );
      await delayMs(backoff);
    }
  }
};
